import type { CSSProperties } from 'react';
import { usePropertyField } from '@vibesites/runtime/react';
import { parseHexColor } from './useColor4';
import { contrastRatio, darkenToContrast, toHex, WCAG_AA_BODY, type RGB } from '../utils/contrast';

const WHITE: RGB = [255, 255, 255];
const DARK = '#1a1a1a';
const DARK_RGB: RGB = [0x1a, 0x1a, 0x1a];

/**
 * The platform keeps white `--vibe-color-text-on-primary` on mid-tone
 * primaries (luminance up to 0.55), which can fall below WCAG AA. Spread the
 * returned style onto a `bg-[var(--vibe-color-primary)]` band to switch its
 * text to dark only when white fails 4.5:1 and dark reads better. Returns
 * undefined when the platform default already passes.
 */
export function useReadableOnPrimary(): CSSProperties | undefined {
  const rgb = parseHexColor(usePropertyField('property_primary_color'));
  if (!rgb) return undefined;
  const onWhite = contrastRatio(rgb, WHITE);
  if (onWhite >= WCAG_AA_BODY) return undefined;
  if (contrastRatio(rgb, DARK_RGB) <= onWhite) return undefined;
  return { '--vibe-color-text-on-primary': DARK } as CSSProperties;
}

/**
 * For `--vibe-color-primary` text on a white band, darkened just enough to
 * reach WCAG AA against white. `'color4'` uses Color 4 when set (else the
 * primary) and replaces `color4.primaryOverride`; `'primary'` keeps the
 * primary. Returns undefined when no remap is needed.
 */
export function useReadablePrimaryOnWhite(source: 'color4' | 'primary'): CSSProperties | undefined {
  const color4 = parseHexColor(usePropertyField('property_color_4'));
  const primary = parseHexColor(usePropertyField('property_primary_color'));
  const preferred = source === 'color4' ? color4 : null;
  const rgb = preferred ?? primary;
  if (!rgb) return undefined;
  const readable = contrastRatio(rgb, WHITE) >= WCAG_AA_BODY ? rgb : darkenToContrast(rgb, WHITE);
  if (!preferred && readable === rgb) return undefined;
  return { '--vibe-color-primary': toHex(readable) } as CSSProperties;
}
